import { useMemo, useState } from "react";
import { Col, Row } from "reactstrap";
import { permissions, roles } from "../../assets/configuration/claimSettings";
import { IControlStyles, PageContent } from "../../assets/layouts";
import { PageActionButtonProps } from "../../assets/layouts/props";
import { ConfirmationDialog } from "../../assets/types/common";
import { ClaimHelper } from "../../assets/utils/helpers";
import { FormatHelper } from "../../assets/utils/helpers/formatHelper";
import { Constants } from "../../assets/utils/lib/constants";
import {
  Dialog,
  IDrawerStyles,
  useDrawerStyles,
  VfxLib,
} from "../../assets/vfx";
import Form from "../../assets/vfx/controls/control";
import { VfxDialogProps } from "../../assets/vfx/dialog/vfxDialogProps";
import { CommunityDto } from "../../services/dto";
import { LookupDto } from "../../assets/utils/lib/lookupDto";
import AddressData from "../common/DisplayData/AddressData";
import DisplayData from "../common/DisplayData/DisplayData";
import { ViewProps } from "./props";
import TabComponent from "../common/Tabs/tabs";
import { UserContainer } from "../account/user/userContainer";

interface CommunityViewProps {
  controlStyles?: IControlStyles | undefined;
  data: CommunityDto | undefined;
  show: boolean;
  lookups: ViewProps.CommunityLookupProps | undefined;
  onHide: () => void;
  onEdit: () => void;
  onDeactivate: () => void;
}

export const CommunityView = (props: CommunityViewProps) => {
  const styles: IDrawerStyles = useDrawerStyles();

  const [dialogProps, setDialogProps] = useState<VfxDialogProps | undefined>(
    undefined,
  );

  let pageActionButtons: PageActionButtonProps[] = [];

  const isSystemAdmin = useMemo(
    () => ClaimHelper.hasRole([roles.SystemAdmin]),
    [],
  );

  if (
    ClaimHelper.hasPermission([
      permissions.SystemFull,
      permissions.ManageCommunity,
    ])
  ) {
    pageActionButtons.push(
      VfxLib.pageActionButtons({ onClick: props.onEdit }).edit,
    );
    pageActionButtons.push(
      VfxLib.pageActionButtons({ onClick: props.onHide }).close,
    );
  }

  function handleDeactivate() {
    setDialogProps(
      new ConfirmationDialog().get(props.onDeactivate, "Deactivate", {
        Name: props.data?.name ?? Constants.emptyString,
      }),
    );
  }

  return (
    <PageContent
      title={props?.data?.name ?? "View Community"}
      actionbuttons={pageActionButtons}
    >
      <Row className="gy-3">
        <Col xs={12} sm={6} lg={props.data?.isActive ? 3 : 4}>
          <DisplayData text="Name" importedData={props?.data?.name} />
          <DisplayData text="Abbreviation" importedData={props?.data?.abbreviation} />

          {props?.data?.description && (
            <DisplayData
              text="Description"
              importedData={props?.data?.description}
            />
          )}
          <AddressData
            text="Address"
            AddressLine1={props.data?.addressLine1}
            AddressLine2={props.data?.addressLine2}
            City={props.data?.city}
            State={
              LookupDto.getLookupById(
                props?.lookups?.state,
                props?.data?.stateId,
              )?.code ?? Constants.emptyString
            }
            ZipCode={props?.data?.zip}
          />
          <DisplayData text="Phone" importedData={props?.data?.contactPhone} />
          <DisplayData text="Email" importedData={props?.data?.contactEmail} />
          {isSystemAdmin && (
            <DisplayData
              text="Publish Date"
              importedData={
                FormatHelper.formatDate1(
                  props.data?.hereNowPublishDate ?? undefined,
                ) ?? "Not Published"
              }
            />
          )}
        </Col>

        <Col xs={12} sm={6} lg={isSystemAdmin ? 3 : 4}>
          <DisplayData
            text="Executive"
            importedData={
              <p style={{ marginBottom: 0, wordBreak: "break-word" }}>
                {props.data?.executiveName ?? Constants.emptyString}
                <br />
                {props.data?.executiveEmail ?? Constants.emptyString}
              </p>
            }
          />
          {(props.data?.contactName ||
            props.data?.contactEmail ||
            props.data?.contactPhone) && (
            <DisplayData
              text="Contact"
              importedData={
                <p style={{ marginBottom: 0, wordBreak: "break-word" }}>
                  {props.data?.contactName ?? Constants.emptyString}
                  <br />
                  {props.data?.contactJobTitle ?? Constants.emptyString}
                  <br />
                  {props.data?.contactEmail ?? Constants.emptyString}
                  <br />
                  {props.data?.contactPhone ?? Constants.emptyString}
                </p>
              }
            />
          )}
        </Col>
        {isSystemAdmin && (
          <Col xs={12} sm={6} lg={3}>
            <div className="mb-2">
              <DisplayData
                text="Total Budget"
                importedData={
                  FormatHelper.formatCurrencyValue(
                    props.data?.totalBudget?.toString(),
                    true,
                  ) ?? Constants.emptyString
                }
              />
            </div>
            <div className="mb-2">
              <DisplayData
                text="General Fund"
                importedData={
                  FormatHelper.formatCurrencyValue(
                    props.data?.generalFund?.toString(),
                    true,
                  ) ?? Constants.emptyString
                }
              />
            </div>
            <div className="mb-2">
              <DisplayData
                text="Population"
                importedData={
                  FormatHelper.formatNumber(
                    props.data?.population?.toString() ?? Constants.emptyString,
                  ) ?? Constants.emptyString
                }
              />
            </div>
            <DisplayData
              text="Next Election"
              importedData={
                FormatHelper.formatDate1(props.data?.nextElectionDate) ??
                Constants.emptyString
              }
            />
          </Col>
        )}

        {isSystemAdmin &&
          props.data?.isActive &&
          props?.data?.activationDate &&
          props?.data?.activationDate.toString() !== "0001-01-01T00:00:00" && (
            <Col xs={12} sm={9} lg={3}>
              <DisplayData
                text={`${
                  props.data?.isActive ? "Activated" : "Deactivated"
                } Date`}
                importedData={FormatHelper.formatDate1(
                  props.data?.isActive
                    ? props?.data?.activationDate
                    : props?.data?.deactivationDate,
                )}
              />
            </Col>
          )}
      </Row>

      <Row className="mt-3">
        <div className="d-flex justify-content-end">
          <Form.Button
            className="btn btn-outline-danger"
            onClick={handleDeactivate}
            type="button"
          >
            <i className="bi bi-ban me-2" style={styles.footerButtonIcon}></i>
            Deactivate
          </Form.Button>
        </div>
      </Row>

      <TabComponent
        name="Forms"
        idName="Community"
        eventKeys={["users-panel"]}
        tabPanes={[
          <UserContainer isCommunityDeactive={!props.data?.isActive} />,
        ]}
        tabNames={["Users"]}
      />

      {dialogProps && (
        <Dialog
          contentText={dialogProps.contentText}
          contentTitle={dialogProps.contentTitle}
          onClose={() => setDialogProps(undefined)}
          onConfirm={() => {
            setDialogProps(undefined);
            dialogProps?.onConfirm();
          }}
          open={dialogProps !== undefined}
          closeText={dialogProps.closeText}
          confirmText={dialogProps.confirmText}
        />
      )}
    </PageContent>
  );
};
